import { HeaderPage } from "@/components/HeaderPage";
import { Card } from "@/components/ui/card";
import { MapFilters } from "@/components/MapFilters";
import { OccurrencesGoogleMap } from "@/components/OccurrencesGoogleMap";
import { AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { useGetOcurrences } from "@/hooks/useOccurrences";
import { useGetCategories } from "@/hooks/useCategories";
import { useState, useMemo } from "react";
import type { OccurrenceDto } from "@/dtos/CategoriesResponseDto";
import { Loading } from "@/components/Loading";

type MarkerData = {
  id: string;
  protocol: string;
  category: string;
  status: string;
  icon: typeof AlertTriangle;
  lat: number;
  lng: number;
  title: string;
  description: string;
  address: string;
  priority: string;
};

const statusConfig: Record<string, { label: string }> = {
  AWAITING_ANALYSIS: { label: "Aguardando Análise" },
  IN_PROGRESS: { label: "Em Andamento" },
  RESOLVED: { label: "Resolvida" },
  // REJECTED: { label: "Rejeitada" },
  CANCELLED: { label: "Cancelada" },
  REOPENED: { label: "Reaberta" },
  RETURNED: { label: "Devolvida" },
  AWAITING_REANALYSIS: { label: "Aguardando Reanálise" },
  FILED: { label: "Arquivada" },
};

export const Mapa = () => {
  const [status, setStatus] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [priority, setPriority] = useState("");

  const { data: occurrencesData, isLoading: isLoadingOccurrences } =
    useGetOcurrences({
      page: 1,
      limit: 100,
      status: status || undefined,
      categoryId: categoryId || undefined,
      priority: priority || undefined,
    });

  const { data: categoriesData } = useGetCategories();

  console.log("Categories Data:", categoriesData); // Debug temporário

  const occurrences: OccurrenceDto[] = useMemo(
    () => occurrencesData?.data || [],
    [occurrencesData],
  );
  const categories = useMemo(() => {
    // Verificar se categoriesData é um array ou objeto com data
    if (Array.isArray(categoriesData)) {
      return categoriesData;
    }
    if (categoriesData?.data && Array.isArray(categoriesData.data)) {
      return categoriesData.data;
    }
    return [];
  }, [categoriesData]);

  // Calcular estatísticas por categoria
  const categoryStats = useMemo(() => {
    const stats: Record<string, number> = {};
    occurrences.forEach((occ) => {
      const catName = occ.category?.name || "Outros";
      stats[catName] = (stats[catName] || 0) + 1;
    });
    return stats;
  }, [occurrences]);

  // Converter ocorrências em marcadores usando latitude/longitude
  const markers = useMemo(() => {
    const validMarkers = occurrences
      .filter((occ) => {
        // Verificar se tem latitude e longitude válidas
        if (!occ.latitude || !occ.longitude) {
          return false;
        }

        const lat =
          typeof occ.latitude === "string"
            ? parseFloat(occ.latitude)
            : occ.latitude;
        const lng =
          typeof occ.longitude === "string"
            ? parseFloat(occ.longitude)
            : occ.longitude;

        const isValid =
          !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng);

        return isValid;
      })
      .map((occ) => {
        const lat =
          typeof occ.latitude === "string"
            ? parseFloat(occ.latitude)
            : occ.latitude!;
        const lng =
          typeof occ.longitude === "string"
            ? parseFloat(occ.longitude)
            : occ.longitude!;

        return {
          id: occ.id,
          protocol: occ.protocol,
          category: occ.category?.name || "Outros",
          status: occ.status,
          icon: AlertTriangle,
          lat,
          lng,
          title: `${occ.category?.name || "Ocorrência"} - ${occ.protocol}`,
          description: occ.description || "Sem descrição",
          address: occ.address,
          priority: occ.priority,
        };
      });

    console.log("Total de ocorrências:", occurrences.length);
    console.log("Marcadores válidos:", validMarkers.length);
    if (validMarkers.length === 0) {
      console.warn(
        "⚠️ Nenhuma ocorrência possui coordenadas válidas para exibir no mapa",
      );
    }

    return validMarkers;
  }, [occurrences]);

  // Calcular centro do mapa baseado nos marcadores
  const mapCenter: [number, number] = useMemo(() => {
    if (markers.length === 0) return [-14.235, -51.925]; // Centro do Brasil como padrão

    const avgLat = markers.reduce((sum, m) => sum + m.lat, 0) / markers.length;
    const avgLng = markers.reduce((sum, m) => sum + m.lng, 0) / markers.length;

    // Validar se os valores são finitos
    if (!isFinite(avgLat) || !isFinite(avgLng)) {
      return [-14.235, -51.925]; // Fallback para centro do Brasil
    }

    return [avgLat, avgLng];
  }, [markers]);

  // Calcular zoom baseado nos marcadores
  const mapZoom = useMemo(() => {
    if (markers.length === 0) return 4; // Zoom para ver todo o Brasil
    return 12; // Zoom mais próximo quando há marcadores
  }, [markers.length]);

  if (isLoadingOccurrences) {
    return <Loading label="Carregando mapa de ocorrências..." />;
  }

  return (
    <>
      <HeaderPage
        title="Mapa de Ocorrências"
        description="Visualize a distribuição geográfica das ocorrências"
      />

      <div className="grid gap-4 lg:grid-cols-4 mt-6">
        {/* Sidebar filters */}
        <motion.div
          className="lg:col-span-1"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <MapFilters
            status={status}
            setStatus={setStatus}
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            priority={priority}
            setPriority={setPriority}
            categories={categories}
            categoryStats={categoryStats}
          />
        </motion.div>

        {/* Map Area */}
        <motion.div
          className="lg:col-span-3"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="relative overflow-hidden h-[600px]">
            {markers.length === 0 && !isLoadingOccurrences && (
              <div className="absolute inset-0 flex items-center justify-center z-10 bg-background/80 backdrop-blur-sm">
                <div className="text-center p-6">
                  <p className="text-lg font-medium text-muted-foreground mb-2">
                    Nenhuma ocorrência com localização disponível
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {occurrences.length > 0
                      ? `${occurrences.length} ocorrência(s) encontrada(s), mas nenhuma possui coordenadas GPS`
                      : "Nenhuma ocorrência encontrada com os filtros aplicados"}
                  </p>
                </div>
              </div>
            )}
            <OccurrencesGoogleMap
              markers={markers}
              center={mapCenter}
              zoom={mapZoom}
              statusConfig={statusConfig}
            />

            {/* Stats overlay */}
            <motion.div
              className="absolute bottom-4 left-4 right-4 flex gap-2 flex-wrap z-1000"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <div className="bg-card/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                <span className="text-xs font-medium">
                  {markers.length} ocorrências no mapa
                </span>
              </div>
            </motion.div>
          </Card>
        </motion.div>
      </div>
    </>
  );
};
